import React, { useContext, useEffect, useState } from 'react';
import moment from 'moment';

import { DataContext } from '../../../stores/covering-table-store';
import WeekHelper from '../../../functions/WeekHelper';

import { DeepCopy } from '../shared/TableEventHelpers';

import AssignmentEditorContainer from './assignment_editor/AssignmentEditorContainer';
import CorruptAssignmentsAlert from './corrupt_assignments/CorruptAssignmentsAlert';
import RemarkContainer from './RemarkContainer';

const RowDetail = ({ row }) => {
    const {
        store: { hiddenColumns, noWeeksColumns, startWeek, endWeek, weekWidth },
    } = useContext(DataContext);

    const [isNameShowing, setIsNameShowing] = useState(!hiddenColumns.includes('empName'));

    let intStartWeek = parseInt(WeekHelper.GetWeek(startWeek));
    let intEndWeek = parseInt(WeekHelper.GetWeek(endWeek));
    let weeks = WeekHelper.GetWeeksBetween(WeekHelper.GetWeek(startWeek), WeekHelper.GetWeek(endWeek));
    let matrixLength = weeks.length;

    const DetailBody = DetailTableBody(row, intStartWeek, intEndWeek, weeks, matrixLength, weekWidth);
    const paddingVal = CalculatePadding(noWeeksColumns, hiddenColumns, isNameShowing);
    let width = weekWidth * matrixLength;

    const style = {
        width: width,
        minWidth: width,
        maxWidth: width,
    };

    useEffect(() => {
        setIsNameShowing(!hiddenColumns.includes('empName'));
    }, [hiddenColumns, noWeeksColumns]);

    return (
        <>
            <div className='d-flex flex-row padding-top-'>
                <RemarkContainer isNameShowing={isNameShowing} row={row} />
                <div>
                    <div
                        className='employee-covering-info row-detail-height border-radius-week-cells py-0 pr-0 m-0'
                        style={{ paddingLeft: paddingVal }}>
                        <div className='row row-detail-table-height border-radius-week-cells'>
                            <table className='border-all-around border-radius-week-cells p-0 m-0' style={style}>
                                <thead />
                                <tbody className='border-radius-week-cells p-0 m-0' style={style}>
                                    {DetailBody}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    {row.assignments.some((a) => a.isCorrupt) && (
                        <div style={{ paddingLeft: paddingVal - 12 }}>
                            <CorruptAssignmentsAlert row={row} />
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default RowDetail;

const DetailTableBody = (row, startYearWeek, endYearWeek, weeks, matrixLength, weekWidth) => {
    var tableRowMatrix = [];

    var activityMatrix = CreateActivityMatrix(matrixLength);
    var alteredAssignments = DeepCopy(row.assignments);

    alteredAssignments.forEach((assignment) => {
        if (assignment.endWek < startYearWeek || assignment.begWek > endYearWeek) {
            assignment.isAddedToMatrix = true;
        } else if (assignment.isCorrupt) {
            assignment.isAddedToMatrix = true;
        } else {
            assignment.isAddedToMatrix = false;
        }
    });

    var intYearWeeks = weeks.map((element) => {
        return parseInt(element);
    });

    for (var rowIndex = 0; rowIndex < 4; rowIndex++) {
        let resultRow = GenerateTableRow(
            row,
            rowIndex,
            activityMatrix,
            alteredAssignments,
            matrixLength,
            startYearWeek,
            endYearWeek,
            intYearWeeks,
            weekWidth
        );
        if (resultRow.length > 0) {
            tableRowMatrix.push(
                <tr key={rowIndex + '-row'} className='row-detail-tablecell p-0 m-0'>
                    {resultRow}
                </tr>
            );
        } else {
            tableRowMatrix.push(<tr key={rowIndex + '-row'} className='row-detail-tablecell p-0 m-0' />);
        }
    }

    return tableRowMatrix;
};

const GenerateTableRow = (
    row,
    rowIndex,
    activityMatrix,
    assignments,
    matrixLength,
    startYearWeek,
    endYearWeek,
    intYearWeeks,
    weekWidth
) => {
    const tableRow = [];

    for (var colIndex = 0; colIndex < matrixLength; colIndex++) {
        let addedAssignment = false;

        const addedAssignment_colIndex = MapAssignmentsToRow(
            row,
            tableRow,
            rowIndex,
            colIndex,
            assignments,
            addedAssignment,
            activityMatrix,
            startYearWeek,
            endYearWeek,
            intYearWeeks,
            weekWidth
        );
        addedAssignment = addedAssignment_colIndex[0];
        colIndex = addedAssignment_colIndex[1];

        if (!addedAssignment && !activityMatrix[rowIndex][colIndex]) {
            let className = 'c-white border-radius-10px create-assignment-cell';

            const style = {
                width: weekWidth,
                minWidth: weekWidth,
                maxWidth: weekWidth,
            };

            const key = rowIndex + ' - ' + colIndex;
            tableRow.push(TableCell(row, key, className, style, 1, 1, '', null, intYearWeeks[colIndex].toString()));
        }
    }

    return tableRow;
};

const MapAssignmentsToRow = (
    row,
    tableRow,
    rowIndex,
    colIndex,
    assignments,
    addedAssignment,
    activityMatrix,
    startYearWeek,
    endYearWeek,
    intYearWeeks,
    weekWidth
) => {
    assignments.map((assignment) => {
        if (addedAssignment) {
            return null;
        }

        if (!activityMatrix[rowIndex][colIndex] && !assignment['isAddedToMatrix']) {
            const assignmentStartWeek = parseInt(assignment.begWek);
            let startWeek = Math.max(assignmentStartWeek, startYearWeek);
            let endWeek = Math.min(parseInt(assignment.endWek), endYearWeek);

            if (intYearWeeks[colIndex] === startWeek) {
                let rowSpan = assignment.rowSpan;
                let colSpan = WeekHelper.GetNumberOfWeeksBetween(startWeek.toString(), endWeek.toString());

                // Occupy activityMatrix with current assignment
                for (let i = rowIndex; i < rowIndex + rowSpan; i++) {
                    for (let j = colIndex; j < colIndex + colSpan; j++) {
                        activityMatrix[i][j] = true;
                    }
                }

                Object.assign(assignment, { isAddedToMatrix: true });

                let height = rowSpan * 30;
                let width = weekWidth * colSpan;
                let textContent = '';
                let className = 'c-white border-radius-week-cells';

                const style = {
                    textAlign: 'left',
                    height: height,
                    minHeight: height,
                    maxHeight: height,
                    width: width,
                    minWidth: width,
                    maxWidth: width,
                    padding: '0.15rem',
                };

                if (assignment.isEditable === false) {
                    className += ' with-border week-gray';
                    textContent = '';
                } else if (assignment['bltID'] === 'SM' || assignment['bltID'] === 'FL' || assignment['bltID'] === 'TL') {
                    className += ' with-border week-black';
                    assignment['dsc'] ? (textContent = assignment['dsc']) : (textContent = assignment['bltName']);
                } else {
                    className += ' with-border week-green';
                    assignment['dsc'] ? (textContent = assignment['dsc']) : (textContent = '');
                }

                let key = rowIndex + '-' + colIndex;
                tableRow.push(TableCell(row, key, className, style, rowSpan, colSpan, textContent, assignment));

                addedAssignment = true;
                colIndex += colSpan - 1;
            }
        }

        return null;
    });

    return [addedAssignment, colIndex];
};

const TableCell = (row, key, className, style, rowSpan, colSpan, textContent, originalAssignment, createStartWeek = null) => {
    return (
        <td key={key} className={className} style={style} rowSpan={rowSpan} colSpan={colSpan}>
            {originalAssignment?.isEditable || createStartWeek ? (
                <AssignmentEditorContainer
                    row={row}
                    outerDivStyle={style}
                    outerDivClassName='border-radius-10px row-detail-text-padding'
                    textContent={textContent}
                    initialAssignment={originalAssignment}
                    createStartWeek={createStartWeek}
                />
            ) : (
                <div style={style} className='border-radius-10px row-detail-text-padding' />
            )}
        </td>
    );
};

const CreateActivityMatrix = (matrixLength) => {
    var activityMatrix = [];
    for (var rowIndex = 0; rowIndex < 4; rowIndex++) {
        activityMatrix.push(new Array(matrixLength).fill(false));
    }

    return activityMatrix;
};

const CalculatePadding = (noWeeksColumns, hiddenColumns, isNameShowing) => {
    var paddingArr = [];

    noWeeksColumns.map((column) => {
        if (!hiddenColumns.includes(column.name)) {
            paddingArr.push(column);
        }
        return null;
    });

    let paddingVal = 58;

    paddingArr.map((column) => {
        switch (column.name) {
            case 'empName':
                paddingVal += 230;
                break;
            case 'bossName':
                paddingVal += 230;
                break;
            case 'empSkill':
                paddingVal += 170;
                break;
            case 'office':
                paddingVal += 230;
                break;
            case 'regionName':
                paddingVal += 230;
                break;
            case 'status':
                paddingVal += 230;
                break;
            default:
                alert('There was no matching column name in RowDetail Coverings');
                break;
        }
        return null;
    });

    if (isNameShowing) {
        paddingVal -= 270;
    }

    return paddingVal;
};
